import React, { useEffect, useState } from "react";
import axios from "axios";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import TopNav from "../../components/navigation/TopNav";
import StandardScreen, { ScreenContent } from "../../components/layout/StandardScreen";
import Stack from "../../components/layout/Stack";
import Card from "../../components/ui/Card";
import Button from "../../components/ui/Button";
import DashboardGrid from "../../components/dashboard/DashboardGrid";
import KpiCard from "../../components/dashboard/KpiCard";
import ChartCard from "../../components/dashboard/ChartCard";
import EChart from "../../components/dashboard/EChart";

const API_URL = process.env.REACT_APP_API_URL || "";
const METRICS = [
  ["tickets_used", "Average tickets used / day", "Trailing 30-day daily average", "tickets"],
  ["money_received", "Money received (TL)", "Trailing 30-day total", "TL"],
  ["outstanding_tickets", "Outstanding passenger tickets", "End-of-day ticket balances", "tickets"],
  ["obligations_days", "Obligations (days)", "Outstanding tickets / 30-day daily consumption", "days"],
  ["low_balance_users", "Low-balance active users", "Used tickets in 7 days; balance ≤ 2", "users"],
  ["unhappy_users", "Inactive users (60 days)", "No ticket use in preceding 60 days", "users"],
];
const fmt = (value, unit) => value === null || value === undefined ? "—" : `${Number(value).toLocaleString(undefined, { maximumFractionDigits: 2 })}${unit === "TL" ? " TL" : ""}`;
const chartOption = (rows, unit) => ({
  tooltip: { trigger: "axis", valueFormatter: (v) => v === null || v === undefined ? "—" : fmt(v, unit) },
  legend: { data: ["Actual"], bottom: 0 },
  grid: { left: 55, right: 20, top: 25, bottom: 65, containLabel: true },
  xAxis: { type: "category", boundaryGap: false, data: rows.map((r) => r.date.slice(5)), axisLabel: { rotate: 40 } },
  yAxis: { type: "value", scale: true },
  series: [
    { name: "Actual", type: "line", data: rows.map((r) => r.chart_value), showSymbol: false, lineStyle: { width: 3 } },
  ],
});

export default function AdminDashboard() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  useEffect(() => {
    let cancelled = false;
    const load = async () => {
      setLoading(true);
      try {
        const token = localStorage.getItem("token");
        const response = await axios.get(`${API_URL}/admin/dashboard`, { headers: { Authorization: `Bearer ${token}` } });
        if (!cancelled) { setData(response.data); setError(""); }
      } catch (err) {
        if (!cancelled) setError(err.response?.data?.detail || err.message || "Dashboard unavailable");
      } finally { if (!cancelled) setLoading(false); }
    };
    load();
    return () => { cancelled = true; };
  }, []);
  return (
    <StandardScreen background="default" topNav={<TopNav title={t("admin_dashboard.title")} mode="main" />}>
      <ScreenContent>
        <div style={{ width: "100%", maxWidth: 1320, margin: "0 auto" }}>
          <Stack gap="4">
            <Card title={t("admin_dashboard.title")}><p style={{ margin: 0 }}>{t("admin_dashboard.subtitle")}</p></Card>
            {loading && <Card>Loading dashboard…</Card>}
            {error && <Card><p role="alert" style={{ margin: 0 }}>{String(error)}</p></Card>}
            {data && <>
              <DashboardGrid columns={3}>
                {METRICS.map(([key, title, subtitle, unit]) => {
                  const rows = data.series?.[key] || [];
                  return <KpiCard key={key} title={title} value={fmt(rows[rows.length - 1]?.value, unit)} subtitle={subtitle} />;
                })}
              </DashboardGrid>
              <DashboardGrid columns={2}>
                {METRICS.map(([key, title, subtitle, unit]) => (
                  <ChartCard key={key} title={title} subtitle={`${subtitle} · 30 days · carry forward on no-usage days`}>
                    <EChart option={chartOption(data.series?.[key] || [], unit)} />
                  </ChartCard>
                ))}
              </DashboardGrid>
              {data.warning && <p style={{ fontSize: 12, opacity: 0.7 }}>{data.warning}</p>}
            </>}
            <div style={{ width: "100%", maxWidth: 720, margin: "0 auto" }}>
              <Card>
                <Stack gap="3">
                  <h2 style={{ margin: 0, fontSize: "1.2rem", fontWeight: 700 }}>{t("admin_dashboard.frequently_used.title")}</h2>
                  <p style={{ margin: 0 }}>{t("admin_dashboard.frequently_used.subtitle")}</p>
                  <Stack gap="2">
                    <Button fullWidth onClick={() => navigate("/admin/create-payer-wallet")}>{t("admin_dashboard.frequently_used.label2")}</Button>
                    <Button fullWidth onClick={() => navigate("/admin/payers")}>{t("admin_dashboard.frequently_used.label3")}</Button>
                    <Button fullWidth onClick={() => navigate("/admin/detailed-logs")}>{t("admin_dashboard.frequently_used.label4")}</Button>
                    <Button fullWidth onClick={() => navigate("/admin/where-is-my-bus")}>{t("admin_dashboard.frequently_used.label5")}</Button>
                    <Button fullWidth onClick={() => navigate("/admin/ticket-meter-in-time")}>{t("admin_dashboard.frequently_used.label1")}</Button>
                  </Stack>
                </Stack>
              </Card>
            </div>
          </Stack>
        </div>
      </ScreenContent>
    </StandardScreen>
  );
}
